import {
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import {
  Member,
  Role,
  useLazyGetRolesQuery,
  useUpdateMemberRoleMutation,
} from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./OrgUpdateRolesModal.styles";

const validationSchema = Yup.object().shape({
  role: Yup.string().required("Role is required"),
});

interface OrgUpdateRolesModalProps {
  orgId: string;
  member?: Member;
  fetchMembers: () => void;
  open: boolean;
  onClose: () => void;
}

export interface OrgUpdateRolesFormValues {
  role: string;
}

const OrgUpdateRolesModal = (props: OrgUpdateRolesModalProps) => {
  const classes = useStyles();

  const [getRoles] = useLazyGetRolesQuery();

  const [updateRole, { isLoading }] = useUpdateMemberRoleMutation();

  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const { roles } = await getRoles().unwrap();
      setRoles(roles);
    };

    fetchData();
  }, [getRoles]);

  const formik = useFormik({
    initialValues: {
      role: "",
    },
    validationSchema,
    onSubmit: async (values: OrgUpdateRolesFormValues) => {
      const result = await updateRole({
        organizationId: props.orgId,
        memberId: props.member?.memberId ?? "",
        role: values.role,
      }).unwrap();

      if (result.success) {
        props.fetchMembers();
        formik.resetForm();
        props.onClose();
      } else {
        alert("Error updating roles");
      }
    },
  });

  const { values, handleSubmit, setFieldValue } = formik;

  return (
    <RFModal {...props} boxProps={{ className: classes.container }}>
      <Grid
        container
        component={"form"}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <Typography variant="h5">Update Roles</Typography>

        <Grid container item marginTop={theme.spacing(4)} spacing={2}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>User Name</InputLabel>
            <TextField
              InputProps={{
                className: classes.input,
              }}
              variant="outlined"
              fullWidth
              value={`${props.member?.firstName} ${props.member?.lastName}`}
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Role</InputLabel>
            <Select
              className={classes.input}
              variant="outlined"
              fullWidth
              value={values.role}
              onChange={(e) => setFieldValue("role", e.target.value)}
            >
              {[...roles]
                .sort((a, b) => b.level - a.level)
                .map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.description}
                  </MenuItem>
                ))}
            </Select>
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent={"space-between"}
            marginTop={theme.spacing(4)}
          >
            <Grid item>
              <Button
                color="secondary"
                className={` ${classes.button} ${classes.backButton}`}
                onClick={props.onClose}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                className={classes.button}
                loading={isLoading}
                label="Save"
                type="submit"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default OrgUpdateRolesModal;
